import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

type AuthView = 'login' | 'forgot' | 'request' | 'victimSignup';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<AuthView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Request Access state
  const [reqName, setReqName] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqPassword, setReqPassword] = useState('');
  const [reqConfirmPassword, setReqConfirmPassword] = useState('');
  const [showReqPassword, setShowReqPassword] = useState(false);
  const [showReqConfirm, setShowReqConfirm] = useState(false);
  const [reqError, setReqError] = useState('');
  const [reqSubmitted, setReqSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [victimName, setVictimName] = useState('');
  const [victimEmail, setVictimEmail] = useState('');
  const [victimPassword, setVictimPassword] = useState('');
  const [victimConfirmPassword, setVictimConfirmPassword] = useState('');
  const [victimError, setVictimError] = useState('');
  const [victimSuccess, setVictimSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(
        "https://guardiancloudcare.com/backend/api/login.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            email,
            password
          })
        }
      );

      const data = await res.json();

      setLoading(false);

      if (data.success) {

        const role = String(data.user.role)
          .trim()
          .toLowerCase();

        console.log("LOGIN USER:", data.user);
        console.log("ROLE:", role);

        // Save logged-in user
        login(data.user);

        setShowSuccess(true);

        setTimeout(() => {

          if (role === "admin") {

            navigate("/admin-dashboard", {
              replace: true
            });

          } else if (role === "supervisor") {

            navigate("/supervisor-dashboard", {
              replace: true
            });

          } else if (role === "social_worker") {

            navigate("/sw-dashboard", {
              replace: true
            });

          } else if (role === "victim") {

            navigate("/v-dashboard", {
              replace: true
            });

          } else {

            navigate("/dashboard", {
              replace: true
            });

          }

        }, 1000);

      } else {

        setError(data.message || "Login failed");

      }

    } catch (err) {

      console.error("LOGIN ERROR:", err);

      setLoading(false);
      setError("Server error");

    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);
    setForgotSent(true);
  };

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setReqError('');

    if (!reqName || !reqEmail || !reqPassword) {
      setReqError("All fields required");
      return;
    }

    if (reqPassword !== reqConfirmPassword) {
      setReqError("Passwords do not match");
      return;
    }

    try {
      const res = await fetch("https://guardiancloudcare.com/backend/api/request_access.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: reqName,
          email: reqEmail,
          password: reqPassword,
          role: "social_worker" // or dynamic later
        })
      });

      const data = await res.json();

      if (data.success) {
        setReqSubmitted(true);
      } else {
        setReqError("Failed to submit request");
      }

    } catch {
      setReqError("Server error");
    }
  };

  const handleVictimSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setVictimError('');

    if (!victimName || !victimEmail || !victimPassword || !victimConfirmPassword) {
      setVictimError('All fields are required');
      return;
    }

    if (victimPassword.length < 8) {
      setVictimError('Password must be at least 8 characters');
      return;
    }

    if (victimPassword !== victimConfirmPassword) {
      setVictimError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('https://guardiancloudcare.com/backend/api/victim_signup.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: victimName,
          email: victimEmail,
          password: victimPassword,
        }),
      });

      const text = await res.text();
      console.log("Victim signup response:", text);

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        setLoading(false);
        setVictimError("Backend returned invalid JSON. Check victim_signup.php.");
        return;
      }


      if (data.success) {
        setLoading(false);
        setVictimSuccess(true);
      } else {
        setLoading(false);
        setVictimError(data.message || 'Signup failed');
      }
    } catch {
      setLoading(false);
      setVictimError('Server error');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-2/5 flex-col justify-between p-10 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Philippine%20government%20social%20workers%20helping%20children%20in%20a%20community%20center%2C%20warm%20natural%20light%2C%20professional%20documentary%20photography%2C%20soft%20bokeh%20background%2C%20teal%20and%20green%20tones%2C%20compassionate%20atmosphere%2C%20child%20protection%20services&width=800&height=1200&seq=login-bg-001&orientation=portrait"
            alt="Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419]/80 via-[#1a2332]/70 to-[#0f1419]/90" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <img src="https://public.readdy.ai/ai/img_res/6c7f5cc2-f4c3-4618-bf64-1a758112751d.png" alt="DCPCMS" className="w-14 h-14 rounded-xl object-contain" />
            <div>
              <p className="text-white font-bold text-lg leading-tight">DCPCMS</p>
              <p className="text-white/50 text-xs">Digital Child Protection</p>
            </div>
          </div>
          <h2 className="text-white text-3xl font-bold leading-tight mb-4">
            Child Protection<br />Case Management<br />System
          </h2>
          <p className="text-white/60 text-sm leading-relaxed max-w-xs">
            A secure, digital platform for social workers, supervisors, and administrators to manage child protection cases efficiently and compassionately.
          </p>
        </div>
        <div className="relative z-10">
          <div className="grid grid-cols-2 gap-3 mb-8">
            {[
              { icon: 'ri-shield-check-line', label: 'Data Privacy Act 2012' },
              { icon: 'ri-lock-line', label: 'End-to-End Encrypted' },
              { icon: 'ri-eye-off-line', label: 'Role-Based Access' },
              { icon: 'ri-file-shield-line', label: 'Full Audit Trail' },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                <i className={`${b.icon} text-teal-400 text-sm`} />
                <span className="text-white/60 text-xs">{b.label}</span>
              </div>
            ))}
          </div>
          <p className="text-white/30 text-xs">© 2026 DSWD — Department of Social Welfare and Development<br />Republic of the Philippines</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8 justify-center">
            <img src="https://public.readdy.ai/ai/img_res/6c7f5cc2-f4c3-4618-bf64-1a758112751d.png" alt="DCPCMS" className="w-12 h-12 rounded-xl object-contain" />
            <div>
              <p className="font-bold text-gray-800 text-lg">DCPCMS</p>
              <p className="text-gray-400 text-xs">Child Protection System</p>
            </div>
          </div>

          {/* Login Form */}
          {view === 'login' && (
            <>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Welcome Back</h3>
              <p className="text-gray-500 text-sm mb-6">Sign in to access the case management system</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Email Address</label>

                  <div className="relative">
                    <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your.email@dswd.gov.ph"
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Password</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                      className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                      <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="rounded border-gray-300 text-teal-500" />
                    <span className="text-sm text-gray-600">Remember me</span>
                  </label>
                  <button type="button" onClick={() => { setView('forgot'); setError(''); }} className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap">
                    Forgot password?
                  </button>
                </div>
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                    <i className="ri-error-warning-line text-red-500 text-sm" />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}
                <button type="submit" disabled={loading}
                  className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                  {loading ? <><i className="ri-loader-4-line animate-spin" /><span>Signing in...</span></> : <><i className="ri-login-box-line" /><span>Sign In</span></>}
                </button>
              </form>

              {/* Request Access Link */}
              <div className="mt-5 text-center">
                <p className="text-sm text-gray-500">
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setView('request'); setError(''); setReqError(''); setReqSubmitted(false); }}
                    className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer whitespace-nowrap"
                  >
                    Request Access
                  </button>
                </p>
              </div>
              <div className="mt-3 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setView('victimSignup');
                        setError('');
                        setVictimError('');
                        setVictimSuccess(false);
                      }}
                      className="text-sm text-purple-600 hover:text-purple-700 font-semibold cursor-pointer"
                    >
                      Sign up as Victim
                    </button>
                  </div>
              <p className="text-center text-xs text-gray-400 mt-3">Protected under RA 10173 (Data Privacy Act of 2012).</p>
            </>
          )}

          {view === 'victimSignup' && (
          <>
            <button
              onClick={() => {
                setView('login');
                setVictimSuccess(false);
                setVictimError('');
              }}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line" />
              Back to Sign In
            </button>

            {!victimSuccess ? (
              <>
                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-5">
                  <i className="ri-user-heart-line text-purple-500 text-2xl" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  Victim Sign Up
                </h3>

                <p className="text-gray-500 text-sm mb-6">
                  Create a victim account. This account is automatically approved.
                </p>

                <form onSubmit={handleVictimSignup} className="space-y-4">
                  <input
                    type="text"
                    value={victimName}
                    onChange={(e) => setVictimName(e.target.value)}
                    placeholder="Full Name"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                  />

                  <input
                    type="email"
                    value={victimEmail}
                    onChange={(e) => setVictimEmail(e.target.value)}
                    placeholder="Email Address"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                  />

                  <input
                    type="password"
                    value={victimPassword}
                    onChange={(e) => setVictimPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                  />

                  <input
                    type="password"
                    value={victimConfirmPassword}
                    onChange={(e) => setVictimConfirmPassword(e.target.value)}
                    placeholder="Confirm Password"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100"
                  />

                  {victimError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                      <p className="text-red-600 text-sm">{victimError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-purple-500 hover:bg-purple-600 disabled:bg-purple-300 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        <span>Creating account...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line" />
                        <span>Create Victim Account</span>
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-checkbox-circle-line text-purple-500 text-3xl" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Account Created!
                </h3>

                <p className="text-gray-500 text-sm mb-6">
                  Your victim account is already approved. You can now login.
                </p>

                <button
                  onClick={() => {
                    setView('login');
                    setEmail(victimEmail);
                    setVictimSuccess(false);
                  }}
                  className="bg-purple-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-purple-600"
                >
                  Go to Login
                </button>
              </div>
            )}
          </>
        )}

          {/* Request Access */}
          {view === 'request' && (
            <>
              <button onClick={() => { setView('login'); setReqSubmitted(false); setReqError(''); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer mb-6">
                <i className="ri-arrow-left-line" />Back to Sign In
              </button>

              {!reqSubmitted ? (
                <>
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-5">
                    <i className="ri-user-add-line text-teal-500 text-2xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Request Access</h3>
                  <p className="text-gray-500 text-sm mb-6">Submit a registration request to your administrator</p>

                  <form onSubmit={handleRequestSubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Full Name</label>
                      <div className="relative">
                        <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="text"
                          value={reqName}
                          onChange={(e) => setReqName(e.target.value)}
                          placeholder="Juan dela Cruz"
                          className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                        />
                      </div>
                    </div>

                    {/* Official Email */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Official Email</label>
                      <div className="relative">
                        <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="email"
                          value={reqEmail}
                          onChange={(e) => setReqEmail(e.target.value)}
                          placeholder="your.email@dswd.gov.ph"
                          className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Password</label>
                      <div className="relative">
                        <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type={showReqPassword ? 'text' : 'password'}
                          value={reqPassword}
                          onChange={(e) => setReqPassword(e.target.value)}
                          placeholder="Min. 8 characters"
                          className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                        />
                        <button type="button" onClick={() => setShowReqPassword(!showReqPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <i className={showReqPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </button>
                      </div>
                      {reqPassword.length > 0 && reqPassword.length < 8 && (
                        <p className="text-xs text-amber-500 mt-1">Password must be at least 8 characters</p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Confirm Password</label>
                      <div className="relative">
                        <i className="ri-lock-password-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type={showReqConfirm ? 'text' : 'password'}
                          value={reqConfirmPassword}
                          onChange={(e) => setReqConfirmPassword(e.target.value)}
                          placeholder="Re-enter password"
                          className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
                        />
                        <button type="button" onClick={() => setShowReqConfirm(!showReqConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                          <i className={showReqConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </button>
                      </div>
                      {reqConfirmPassword.length > 0 && reqPassword !== reqConfirmPassword && (
                        <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                      )}
                      {reqConfirmPassword.length > 0 && reqPassword === reqConfirmPassword && reqPassword.length >= 8 && (
                        <p className="text-xs text-teal-500 mt-1 flex items-center gap-1"><i className="ri-check-line" />Passwords match</p>
                      )}
                    </div>

                    {/* Error */}
                    {reqError && (
                      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                          <i className="ri-error-warning-line text-red-500 text-sm" />
                        <p className="text-red-600 text-sm">{reqError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {loading
                        ? <><i className="ri-loader-4-line animate-spin" /><span>Submitting...</span></>
                        : <><i className="ri-send-plane-line" /><span>Submit Request</span></>
                      }
                    </button>
                  </form>

                  <p className="text-center text-xs text-gray-400 mt-5">
                    Your request will be reviewed by the system administrator before access is granted.
                  </p>
                </>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-checkbox-circle-line text-teal-500 text-3xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Request Submitted!</h3>
                  <p className="text-gray-500 text-sm mb-2">Your access request has been sent to the administrator.</p>
                  <p className="font-semibold text-gray-800 mb-4">{reqEmail}</p>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 mb-6 text-left">
                    <p className="text-teal-700 text-sm font-medium mb-1">What happens next?</p>
                    <ul className="text-teal-600 text-xs space-y-1">
                      <li className="flex items-start gap-1.5"><i className="ri-check-line mt-0.5" />Admin reviews your request</li>
                      <li className="flex items-start gap-1.5"><i className="ri-check-line mt-0.5" />You&apos;ll receive an email once approved</li>
                      <li className="flex items-start gap-1.5"><i className="ri-check-line mt-0.5" />Login with your credentials after approval</li>
                    </ul>
                  </div>
                  <button
                    onClick={() => { setView('login'); setReqSubmitted(false); setReqName(''); setReqEmail(''); setReqPassword(''); setReqConfirmPassword(''); }}
                    className="bg-teal-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Back to Sign In
                  </button>
                </div>
              )}
            </>
          )}

          {/* Popup Modal */}
          {showSuccess && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
              <div className="bg-white rounded-2xl p-6 w-[320px] text-center shadow-xl animate-fadeIn">
                
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-teal-500 text-3xl"></i>
                </div>

                <h3 className="text-lg font-bold text-gray-800 mb-1">
                  Login Successful
                </h3>

                <p className="text-sm text-gray-500 mb-4">
                  Redirecting to dashboard...
                </p>

                <div className="flex justify-center">
                  <i className="ri-loader-4-line animate-spin text-teal-500 text-xl"></i>
                </div>

              </div>
            </div>
          )}

          {/* Forgot Password */}
          {view === 'forgot' && (
            <>
              <button onClick={() => { setView('login'); setForgotSent(false); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 cursor-pointer mb-6">
                <i className="ri-arrow-left-line" />Back to Sign In
              </button>
              {!forgotSent ? (
                <>
                  <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-5">
                    <i className="ri-lock-password-line text-teal-500 text-2xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Reset Password</h3>
                  <p className="text-gray-500 text-sm mb-6">Enter your email address and we will send you a password reset link.</p>
                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Email Address</label>
                      <div className="relative">
                        <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="your.email@dswd.gov.ph"
                          className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all" />
                      </div>
                    </div>
                    <button type="submit" disabled={loading || !forgotEmail}
                      className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                      {loading ? <><i className="ri-loader-4-line animate-spin" /><span>Sending...</span></> : <><i className="ri-send-plane-line" /><span>Send Reset Link</span></>}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-mail-check-line text-teal-500 text-3xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Email Sent!</h3>
                  <p className="text-gray-500 text-sm mb-2">A password reset link has been sent to:</p>
                  <p className="font-semibold text-gray-800 mb-6">{forgotEmail}</p>
                  <p className="text-xs text-gray-400 mb-6">Didn&apos;t receive the email? Check your spam folder or contact your system administrator.</p>
                  <button onClick={() => { setView('login'); setForgotSent(false); }}
                    className="bg-teal-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                    Back to Sign In
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
